/* ═══════════════════════════════════════════════════════════════
   NOVA — INSPECTION MODE CONTROLLER
   Museum-grade product exhibit overlay
   
   FEATURES:
   • Tab switching
   • Focus trap + ESC close
   • Return-to-showroom state (sessionStorage)
   • Keyboard navigation
   • Theme Editor safe
   
   © 2025 Nova's Gems — Iteration 6
   ═══════════════════════════════════════════════════════════════ */

(() => {
  "use strict";

  const VERSION = "1.0.0";
  const STORE_KEY_OFF = "nova_inspection_off";
  const STORE_KEY_RETURN = "nova_return_showroom";
  const SELECTOR = '[data-nova-inspection]';
  const TRIGGER_SELECTOR = '[data-inspection-trigger]';

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GATING
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function isOffByStorage() {
    try { return localStorage.getItem(STORE_KEY_OFF) === "1"; } catch (_) { return false; }
  }

  function enabled() {
    return !isOffByStorage();
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CORE CONTROLLER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function init(inspectionEl) {
    if (!enabled()) return () => {};
    if (inspectionEl.__novaInspectionInit) return () => {};
    inspectionEl.__novaInspectionInit = true;

    const ac = new AbortController();
    const { signal } = ac;

    const dialog = inspectionEl.querySelector('.nova-inspection__dialog');
    const closeBtns = inspectionEl.querySelectorAll('[data-inspection-close]');
    const tabs = inspectionEl.querySelectorAll('[role="tab"]');
    const panels = inspectionEl.querySelectorAll('[role="tabpanel"]');
    const returnBtn = inspectionEl.querySelector('[data-return-showroom]');

    let lastFocusedEl = null;
    let currentTab = 0;

    // ────────────────────────────────────────────────────────────────────────
    // OPEN/CLOSE
    // ────────────────────────────────────────────────────────────────────────

    function open(trigger) {
      lastFocusedEl = trigger || document.activeElement;
      inspectionEl.hidden = false;
      inspectionEl.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus first tab
      setTimeout(() => tabs[0]?.focus(), 100);

      // Check return-to-showroom state
      try {
        const returnUrl = sessionStorage.getItem(STORE_KEY_RETURN);
        if (returnUrl && returnBtn) {
          returnBtn.hidden = false;
          returnBtn.href = returnUrl;
        }
      } catch (_) {}
    }

    function close() {
      inspectionEl.hidden = true;
      inspectionEl.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Restore focus
      if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
        lastFocusedEl.focus();
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // TAB SWITCHING
    // ────────────────────────────────────────────────────────────────────────

    function switchTab(index) {
      tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', String(isActive));
      });

      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      currentTab = index;
    }

    // ────────────────────────────────────────────────────────────────────────
    // FOCUS TRAP
    // ────────────────────────────────────────────────────────────────────────

    function getFocusableElements() {
      if (!dialog) return [];
      return Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    }

    function trapFocus(e) {
      if (inspectionEl.getAttribute('aria-hidden') === 'true') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // EVENT LISTENERS
    // ────────────────────────────────────────────────────────────────────────

    // Close buttons
    closeBtns.forEach(btn => {
      btn.addEventListener('click', close, { signal });
    });

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && inspectionEl.getAttribute('aria-hidden') === 'false') {
        close();
      }
    }, { signal });

    // Tab switching
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => switchTab(index), { signal });
      
      // Keyboard arrow navigation
      tab.addEventListener('keydown', (e) => {
        let newIndex = index;
        if (e.key === 'ArrowRight') {
          newIndex = Math.min(index + 1, tabs.length - 1);
        } else if (e.key === 'ArrowLeft') {
          newIndex = Math.max(index - 1, 0);
        } else {
          return;
        }
        e.preventDefault();
        switchTab(newIndex);
        tabs[newIndex].focus();
      }, { signal });
    });

    // Focus trap
    document.addEventListener('keydown', trapFocus, { signal });

    // ────────────────────────────────────────────────────────────────────────
    // PUBLIC API (for triggers)
    // ────────────────────────────────────────────────────────────────────────

    inspectionEl.__novaInspectionOpen = open;
    inspectionEl.__novaInspectionClose = close;

    // Cleanup
    signal.addEventListener('abort', () => {
      inspectionEl.__novaInspectionInit = false;
      inspectionEl.__novaInspectionOpen = null;
      inspectionEl.__novaInspectionClose = null;
      document.body.style.overflow = '';
    });

    return () => ac.abort();
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // BIND TRIGGERS
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function bindTriggers(root = document) {
    const triggers = root.querySelectorAll(TRIGGER_SELECTOR);
    triggers.forEach(trigger => {
      if (trigger.__novaInspectionBound) return;
      trigger.__novaInspectionBound = true;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const inspection = document.querySelector(SELECTOR);
        if (inspection && inspection.__novaInspectionOpen) {
          inspection.__novaInspectionOpen(trigger);
        }
      });
    });
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // BOOTSTRAP
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function initAll(root = document) {
    if (!enabled()) return () => {};

    const inspections = root.querySelectorAll(SELECTOR);
    const cleanups = [];

    inspections.forEach(el => {
      if (!el.__novaInspectionCleanup) {
        el.__novaInspectionCleanup = init(el);
      }
      cleanups.push(() => {
        try { el.__novaInspectionCleanup && el.__novaInspectionCleanup(); } catch (_) {}
        el.__novaInspectionCleanup = null;
      });
    });

    bindTriggers(root);

    return () => cleanups.forEach(fn => fn());
  }

  function destroyAll(root = document) {
    const inspections = root.querySelectorAll(SELECTOR);
    inspections.forEach(el => {
      if (el.__novaInspectionCleanup) {
        try { el.__novaInspectionCleanup(); } catch (_) {}
        el.__novaInspectionCleanup = null;
      }
      el.__novaInspectionInit = false;
    });
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // LIFECYCLE
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  let cleanup = null;

  function start() {
    if (cleanup) return;
    cleanup = initAll(document);
  }

  function stop() {
    if (cleanup) {
      try { cleanup(); } catch (_) {}
      cleanup = null;
    }
    destroyAll(document);
  }

  // DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { if (enabled()) start(); }, { once: true });
  } else {
    if (enabled()) start();
  }

  // Theme Editor
  document.addEventListener('shopify:section:load', () => {
    stop();
    if (enabled()) start();
  });

  document.addEventListener('shopify:section:unload', () => {
    stop();
  });

  // NovaMotion integration
  if (window.NovaMotion && typeof window.NovaMotion.registerModule === 'function') {
    window.NovaMotion.registerModule(
      'inspection-mode',
      () => { if (enabled()) start(); },
      () => stop()
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PUBLIC API
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  if (typeof window !== 'undefined') {
    window.NovaInspectionMode = {
      __version: VERSION,
      start,
      stop,
      enabled,
      setLocalStorageOff: () => { try { localStorage.setItem(STORE_KEY_OFF, "1"); stop(); } catch (_) {} },
      clearLocalStorageOff: () => { try { localStorage.removeItem(STORE_KEY_OFF); if (enabled()) start(); } catch (_) {} },
      setReturnShowroomUrl: (url) => { try { sessionStorage.setItem(STORE_KEY_RETURN, url); } catch (_) {} }
    };
  }
})();
